import { MutableRefObject, useCallback, useMemo, useRef } from 'react';

import { VirtualItem, useVirtualizer } from '@tanstack/react-virtual';

import { VIRTUAL_OVER_SCAN, VIRTUAL_ROW_HEIGHT } from '../consts';

interface IVirtualTableParams {
  rowsLength: number;
  onLoadMore: (() => Promise<void>) | null;
  loading: boolean;
  error: boolean;
}

interface IVirtualTable {
  handleScroll: () => void;
  layouts: {
    paddingTop: number;
    paddingBottom: number;
  };
  tableRef: MutableRefObject<null>;
  virtualRows: VirtualItem[];
}

export default function useVirtualTable({
  rowsLength,
  onLoadMore,
  loading,
  error,
}: IVirtualTableParams): IVirtualTable {
  const tableRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: rowsLength,
    getScrollElement: () => tableRef.current,
    estimateSize: () => VIRTUAL_ROW_HEIGHT,
    overscan: VIRTUAL_OVER_SCAN,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualHeight = rowVirtualizer.getTotalSize();

  const layouts = useMemo(
    () => ({
      paddingTop: virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0,
      paddingBottom:
        virtualRows.length > 0
          ? virtualHeight - (virtualRows?.[virtualRows.length - 1]?.end || 0)
          : 0,
    }),
    [virtualRows, virtualHeight],
  );

  const handleScroll = useCallback(async () => {
    if (tableRef?.current && !error && !loading) {
      const { scrollTop, scrollHeight, offsetHeight } = tableRef?.current;
      if (Math.round(scrollTop) >= Math.round(scrollHeight - offsetHeight)) {
        await onLoadMore?.();
      }
    }
  }, [error, onLoadMore, loading]);

  return {
    handleScroll,
    layouts,
    tableRef,
    virtualRows,
  };
}
